/* PLP multi-image product cards (collection/search only) */
(function () {
  const DESKTOP_MIN = 1025;
  const TRANSITION_MS = 400;
  const stateByCard = new WeakMap();
  let lastMode = null; // 'desktop' | 'mobile'

  const isDesktop = () => window.innerWidth >= DESKTOP_MIN;
  const isMobile = () => !isDesktop();

  function resolveGridEl(container) {
    if (!container) return document.querySelector('.product-grid-container #product-grid');

    const maybeEl = container && container.nodeType === 1 ? container : null;
    if (maybeEl) {
      if (maybeEl.id === 'ProductGridContainer') {
        return maybeEl.querySelector('#product-grid');
      }
      if (maybeEl.id === 'product-grid') return maybeEl;
      return maybeEl.querySelector('#product-grid');
    }

    // document fallback
    return document.querySelector('.product-grid-container #product-grid');
  }

  function getCardParts(card) {
    return {
      firstImage: card.querySelector('.product-first-image'),
      swiperWrapper: card.querySelector('.product-swiper-wrapper'),
      swiperEl: card.querySelector('.product-card-swiper'),
    };
  }

  function resetInlineImageStyles(card) {
    const { firstImage, swiperWrapper } = getCardParts(card);
    if (firstImage) firstImage.style.removeProperty('display');
    if (swiperWrapper) swiperWrapper.style.removeProperty('display');
  }

  function showFirstImage(card) {
    if (!isMobile()) return; // inline toggles only on mobile
    const { firstImage, swiperWrapper } = getCardParts(card);
    if (firstImage) firstImage.style.setProperty('display', 'block', 'important');
    if (swiperWrapper) swiperWrapper.style.setProperty('display', 'none', 'important');
  }

  function showSwiper(card) {
    if (!isMobile()) return; // inline toggles only on mobile
    const { firstImage, swiperWrapper } = getCardParts(card);
    if (firstImage) firstImage.style.setProperty('display', 'none', 'important');
    if (swiperWrapper) swiperWrapper.style.setProperty('display', 'block', 'important');
  }

  function getImageCount(swiperEl) {
    if (!swiperEl) return 0;
    const countAttr = swiperEl.getAttribute('data-image-count');
    const count = Number(countAttr);
    return Number.isFinite(count) ? count : 0;
  }

  function destroySwiperState(state) {
    if (!state || !state.swiper) return;
    try {
      state.swiper.destroy(true, true);
    } catch (_) {
      // ignore
    }
    state.swiper = null;
  }

  function ensureClickSwipeGuard(card, state, swiperEl) {
    if (!swiperEl || state.clickGuardBound) return;

    const anchor = card.querySelector('a.full-unstyled-link') || card.querySelector('a[href]');
    if (!anchor) return;

    state.clickGuardBound = true;
    state.wasSwipe = false;
    let startX = 0;
    let startY = 0;
    const THRESHOLD_PX = 8;

    swiperEl.addEventListener(
      'touchstart',
      (e) => {
        const t = e.touches && e.touches[0];
        if (!t) return;
        startX = t.clientX;
        startY = t.clientY;
        state.wasSwipe = false;
      },
      { passive: true }
    );

    swiperEl.addEventListener(
      'touchmove',
      (e) => {
        const t = e.touches && e.touches[0];
        if (!t) return;
        const dx = Math.abs(t.clientX - startX);
        const dy = Math.abs(t.clientY - startY);
        if (dx > THRESHOLD_PX || dy > THRESHOLD_PX) {
          state.wasSwipe = true;
        }
      },
      { passive: true }
    );

    anchor.addEventListener(
      'click',
      (e) => {
        if (!state.wasSwipe) return;
        e.preventDefault();
        e.stopPropagation();
        state.wasSwipe = false;
      },
      true
    );
  }

  function createSwiper(card, state) {
    const { swiperEl } = getCardParts(card);
    if (!swiperEl || typeof Swiper === 'undefined') return null;
    if (state.swiper) return state.swiper;

    const scrollbarEl = swiperEl.querySelector('.swiper-scrollbar');
    const enableTouch = isMobile();

    const swiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      allowTouchMove: enableTouch,
      simulateTouch: enableTouch,

      // Helps avoid accidental PDP navigation on swipe (we also use click guard below).
      preventClicks: true,
      preventClicksPropagation: true,

      scrollbar: scrollbarEl ? { el: scrollbarEl, draggable: true } : false,
    });

    state.swiper = swiper;
    ensureClickSwipeGuard(card, state, swiperEl);

    // Mobile swipe GTM: fire on user-initiated slide changes
    swiper.on('slideChangeTransitionEnd', () => {
      if (!isMobile()) return;
      if (typeof window.handlePlpProductImageSwipe !== 'function') return;
      const previousIndex = typeof swiper.previousIndex === 'number' ? swiper.previousIndex : null;
      window.handlePlpProductImageSwipe(card, swiperEl, swiper.activeIndex, previousIndex);
    });

    requestAnimationFrame(() => {
      if (state.swiper && !state.swiper.destroyed) {
        state.swiper.update();
      }
    });

    return swiper;
  }

  function bindDesktopHover(card, state) {
    if (state.desktopHoverBound) return;
    state.desktopHoverBound = true;

    const { swiperEl } = getCardParts(card);
    if (!swiperEl) return;

    card.addEventListener('mouseenter', () => {
      if (!isDesktop()) return;
      if (state.destroyTimer) {
        clearTimeout(state.destroyTimer);
        state.destroyTimer = null;
      }

      // Make swiper wrapper visible before Swiper measures dimensions.
      // Init on first hover (desktop only) and show 2nd image.
      card.classList.add('plp-swiper-active');

      const swiper = createSwiper(card, state);
      if (!swiper) return;

      const beforeIndex = swiper.activeIndex;
      swiper.slideTo(1, 0);

      swiperEl.classList.add('is-zoom-active');

      // Desktop hover GTM (only when slide actually changes)
      if (
        beforeIndex !== 1 &&
        getImageCount(swiperEl) >= 2 &&
        typeof window.handlePlpProductImageHover === 'function'
      ) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (swiper.activeIndex !== beforeIndex) {
              window.handlePlpProductImageHover(card, swiperEl, swiper.activeIndex);
            }
          });
        });
      }
    });

    card.addEventListener('mouseleave', () => {
      if (!isDesktop()) return;

      const { swiperEl: swiperEl2 } = getCardParts(card);
      if (swiperEl2) swiperEl2.classList.remove('is-zoom-active');
      card.classList.remove('plp-swiper-active');

      // Destroy after crossfade completes.
      if (state.destroyTimer) clearTimeout(state.destroyTimer);
      state.destroyTimer = setTimeout(() => {
        destroySwiperState(state);
        state.destroyTimer = null;
      }, TRANSITION_MS);
    });
  }

  function bindMobileZoomOnIndex(card, state) {
    const { swiperEl } = getCardParts(card);
    if (!state.swiper || !swiperEl) return;

    const swiper = state.swiper;
    const sync = () => {
      swiperEl.classList.toggle('is-zoom-active', swiper.activeIndex === 1);
    };

    swiper.on('slideChange', sync);
    sync();
  }

  function markCardInitialized(card) {
    card.setAttribute('data-plp-swiper-init', 'true');
  }

  function initCard(card) {
    const { swiperEl, firstImage } = getCardParts(card);
    if (!swiperEl && !firstImage) return;

    const state = stateByCard.get(card) || {
      swiper: null,
      destroyTimer: null,
      desktopHoverBound: false,
      clickGuardBound: false,
      wasSwipe: false,
    };
    stateByCard.set(card, state);

    const imageCount = swiperEl ? getImageCount(swiperEl) : 0;

    // Always remove stale inline toggles when re-initializing.
    resetInlineImageStyles(card);

    // Skip heavy init if already done for this breakpoint state (new cards won't have this attr).
    if (card.hasAttribute('data-plp-swiper-init') && imageCount > 1) {
      // Still ensure correct mobile/desktop display for single-image vs multi-image.
      if (isMobile()) showSwiper(card);
      return;
    }

    if (imageCount <= 1) {
      // No swiper for single-image products.
      if (isMobile()) {
        showFirstImage(card);
      }
      markCardInitialized(card);
      return;
    }

    if (isDesktop()) {
      // Desktop: default first image visible, swiper created only on hover.
      card.classList.remove('plp-swiper-active');
      swiperEl.classList.remove('is-zoom-active');
      bindDesktopHover(card, state);
      markCardInitialized(card);
    } else {
      // Mobile/tablet: show swiper immediately and keep it alive.
      showSwiper(card);
      if (typeof Swiper === 'undefined') {
        showFirstImage(card);
        return;
      }
      const swiper = createSwiper(card, state);
      if (!swiper) {
        showFirstImage(card);
        return;
      }
      bindMobileZoomOnIndex(card, state);
      markCardInitialized(card);
    }
  }

  function destroyAllInGrid(gridEl) {
    const cards = gridEl ? gridEl.querySelectorAll('.product-card-wrapper') : [];
    cards.forEach((card) => {
      const state = stateByCard.get(card);
      if (state?.destroyTimer) {
        clearTimeout(state.destroyTimer);
        state.destroyTimer = null;
      }

      const { swiperEl } = getCardParts(card);
      card.classList.remove('plp-swiper-active');
      if (swiperEl) swiperEl.classList.remove('is-zoom-active');

      destroySwiperState(state);

      card.removeAttribute('data-plp-swiper-init');
      resetInlineImageStyles(card);
    });
  }

  function initializeProductCardSwipers(container) {
    const gridEl = resolveGridEl(container);
    if (!gridEl) return;

    const modeNow = isDesktop() ? 'desktop' : 'mobile';
    if (lastMode && lastMode !== modeNow) {
      destroyAllInGrid(gridEl);
    }
    lastMode = modeNow;

    // Init cards in current grid. New cards (from facets or infinite scroll) won't have the data attr yet.
    const cards = gridEl.querySelectorAll('.product-card-wrapper');
    cards.forEach((card) => {
      // Skip already initialized cards (requirement: only init new cards after AJAX/infinite scroll).
      if (card.hasAttribute('data-plp-swiper-init')) return;
      initCard(card);
    });
  }

  // Re-init when crossing 1024/1025 breakpoint.
  function onResize() {
    const modeNow = isDesktop() ? 'desktop' : 'mobile';
    if (modeNow === lastMode) return;

    const plpGrid = document.getElementById('ProductGridContainer');
    const gridEl = resolveGridEl(plpGrid || document);
    destroyAllInGrid(gridEl);
    lastMode = modeNow;

    // Re-init after mode switch.
    initializeProductCardSwipers(plpGrid || document);
  }

  function reinitPlpProductCardSwipers(container) {
    const gridEl = resolveGridEl(container);
    destroyAllInGrid(gridEl);
    initializeProductCardSwipers(container);
  }

  // Expose globally for facets/infinite scroll re-init.
  window.initializeProductCardSwipers = initializeProductCardSwipers;
  window.reinitPlpProductCardSwipers = reinitPlpProductCardSwipers;

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      const plpGrid = document.getElementById('ProductGridContainer');
      window.initializeProductCardSwipers(plpGrid || document);

      // Keep existing PLP behavior: sync filter pills on load.
      setTimeout(() => {
        if (typeof window.selectMyFilterMaterial === 'function') window.selectMyFilterMaterial();
      }, 500);
    },
    { once: true }
  );

  window.addEventListener(
    'load',
    () => {
      const plpGrid = document.getElementById('ProductGridContainer');
      if (!plpGrid) return;
      window.initializeProductCardSwipers(plpGrid);
    },
    { once: true }
  );

  window.addEventListener('resize', () => {
    // Avoid excessive work during resize drag.
    window.requestAnimationFrame(onResize);
  });
})();

